import { useMemo, useState } from 'react';
import { useSuppliers } from '../../api/suppliers.js';
import type { Supplier } from '../../api/types.js';
import { formatAmount, formatInvoiceDate } from '../invoices/format.js';
import { SupplierDrawer } from './SupplierDrawer.js';
import { SupplierStatusPill } from './SupplierStatusPill.js';
import { EmptyState } from '../../components/EmptyState.js';
import { PageIdentity, tenantSlug } from '../../shell/PageIdentity.js';
import { PageWrapper } from '../../shell/PageWrapper.js';

// Feature gate for the INSEE prospect tab. Contract has no
// /api/insee/search endpoint yet (REQ-BE-? deferred), so leave the tab
// clickable but inert.
const INSEE_AVAILABLE = false;

type TabKey = 'all' | 'known' | 'prospects';

interface Props {
  tenantId: string;
  tenantName: string;
}

export function AnnuaireScreen({ tenantId, tenantName }: Props) {
  const suppliers = useSuppliers(tenantId);
  const [q, setQ] = useState('');
  const [tab, setTab] = useState<TabKey>('all');
  const [active, setActive] = useState<Supplier | null>(null);

  const known: Supplier[] = useMemo(() => {
    const all = suppliers.data ?? [];
    const sorted = [...all].sort((a, b) => {
      const ta = a.lastInvoiceAt ? new Date(a.lastInvoiceAt).getTime() : 0;
      const tb = b.lastInvoiceAt ? new Date(b.lastInvoiceAt).getTime() : 0;
      return tb - ta;
    });
    const needle = q.trim().toLowerCase();
    if (!needle) return sorted;
    const isDigits = /^\d+$/.test(needle);
    return sorted.filter((s) => {
      if (isDigits) {
        return (s.siren ?? '').includes(needle);
      }
      return (s.name ?? '').toLowerCase().includes(needle);
    });
  }, [suppliers.data, q]);

  const showKnown = tab === 'all' || tab === 'known';
  const showProspects = tab === 'all' || tab === 'prospects';

  return (
    <PageWrapper>
      <section className="annuaire" aria-label="Annuaire">
        <PageIdentity
          testId="annuaire"
          eyebrow={tenantSlug(tenantName)}
          title="Fournisseurs"
          subtitle={
            <>
              Cette liste est <strong>scopée par société</strong> — un fournisseur servant plusieurs
              entités Altyor apparaît dans chacune avec son propre historique.
            </>
          }
        />

        <div className="annuaire__controls">
          <div className="filter-pills" role="tablist" aria-label="Filtres annuaire">
            {/* REQ-FE-029e: status tabs match mockup `07-suppliers.png`
              with `Tous · n / Référencés · n / Nouveaux · n` counts.
              "Nouveaux" is the renamed "Prospects" — wording from the
              mockup. Prospects count is 0 until INSEE_AVAILABLE flips. */}
            {(['all', 'known', 'prospects'] as TabKey[]).map((t) => {
              const allCount = (suppliers.data ?? []).length;
              const count = t === 'all' ? allCount : t === 'known' ? allCount : 0;
              return (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  aria-selected={tab === t}
                  className={`filter-pill${tab === t ? ' is-active' : ''}`}
                  onClick={() => setTab(t)}
                  data-testid={`annuaire-tab-${t}`}
                >
                  <span>{t === 'all' ? 'Tous' : t === 'known' ? 'Référencés' : 'Nouveaux'}</span>
                  <span className="filter-pill__count" data-testid={`annuaire-tab-${t}-count`}>
                    · {count}
                  </span>
                </button>
              );
            })}
          </div>
          <input
            type="search"
            className="modal__search"
            placeholder="Rechercher par nom ou SIREN…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="annuaire-search"
          />
        </div>

        {showKnown ? (
          <section className="annuaire__lane" data-testid="annuaire-lane-known">
            <header className="annuaire__lane-head">
              <h2 className="display annuaire__lane-title">Fournisseurs référencés</h2>
              <span className="muted">{known.length} fournisseurs</span>
            </header>
            {suppliers.isLoading ? (
              <p className="muted" data-testid="annuaire-loading">
                Chargement…
              </p>
            ) : suppliers.isError ? (
              <p className="invoices__error" role="alert">
                Impossible de charger les fournisseurs.
              </p>
            ) : known.length === 0 ? (
              <EmptyState
                testId="annuaire-known-empty"
                variant="card"
                icon="🗂"
                title={
                  (suppliers.data ?? []).length === 0
                    ? 'Aucun fournisseur référencé'
                    : 'Aucun fournisseur ne correspond'
                }
                description={
                  (suppliers.data ?? []).length === 0
                    ? 'Vos fournisseurs apparaîtront ici dès la première facture reçue.'
                    : 'Modifiez votre recherche pour afficher d’autres fournisseurs.'
                }
              />
            ) : (
              <ul className="annuaire-rows" data-testid="annuaire-known-list">
                {known.map((s) => (
                  <li
                    key={`${s.siren ?? 'unknown'}-${s.name ?? ''}`}
                    className="annuaire-row"
                    data-testid={`annuaire-row-${s.siren ?? 'unknown'}`}
                  >
                    <button
                      type="button"
                      className="annuaire-row__btn"
                      onClick={() => setActive(s)}
                    >
                      <span className="annuaire-row__badge">
                        <SupplierStatusPill status="known" />
                      </span>
                      <span className="annuaire-row__name">{s.name ?? '—'}</span>
                      <span className="muted annuaire-row__siren">
                        {s.siren ? `SIREN ${s.siren}` : 'SIREN inconnu'}
                      </span>
                      <span className="annuaire-row__count">
                        {s.invoiceCount} facture{s.invoiceCount > 1 ? 's' : ''}
                      </span>
                      <span className="muted annuaire-row__last">
                        {s.lastInvoiceAt ? formatInvoiceDate(s.lastInvoiceAt) : '—'}
                      </span>
                      <span className="annuaire-row__amount">
                        {formatAmount(s.totalAmount, s.currency ?? 'EUR')}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : null}

        {showProspects ? (
          <section className="annuaire__lane" data-testid="annuaire-lane-prospects">
            <header className="annuaire__lane-head">
              <h2 className="display annuaire__lane-title">Nouveaux prospects (INSEE)</h2>
              {INSEE_AVAILABLE ? null : (
                <span className="pill pill--neutral" data-testid="annuaire-insee-unavailable">
                  Indisponible
                </span>
              )}
            </header>
            {INSEE_AVAILABLE ? (
              <p className="muted">À venir : recherche INSEE par SIREN ou nom.</p>
            ) : (
              <EmptyState
                testId="annuaire-insee-empty"
                variant="inline"
                icon="🔎"
                title="Recherche INSEE bientôt disponible"
                description="La recherche de nouveaux fournisseurs sera activée prochainement."
              />
            )}
          </section>
        ) : null}

        {active ? (
          <SupplierDrawer tenantId={tenantId} supplier={active} onClose={() => setActive(null)} />
        ) : null}
      </section>
    </PageWrapper>
  );
}
